import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import ConsentEventItem from "./ConsentEventItem";
import ConsentRequestDetails from "./ConsentEventDetails/ConsentRequestDetails";
import ConsentActionDetails from "./ConsentEventDetails/ConsentActionDetails";
import {
  CONSENT_STATUS_TYPES,
  CONSENT_VIA_SELECT_OPTIONS,
  CONSENT_ACTION_TYPES,
  CONSENT_EVENT_TYPES,
  CONSENT_RENDERING_TYPES
} from "../../constant/consent";
import {
  formatConsentEventTimestamp,
  mapEventTypeToStatus,
  mapServerEventTypeToDisplayRenderingType,
  getConsentActionUserName
} from "../../utils/eConsentHelper";
import { ReactComponent as CircleCheckIcon } from "../../assets/svg/Check_circle_consent.svg";
import { ReactComponent as CircleCloseIcon } from "../../assets/svg/circle-close.svg";
import { ReactComponent as RemoveIcon } from "../../assets/svg/remove-icon.svg";
import { ReactComponent as AllCyclesIcon } from "../../assets/svg/all-cycles.svg";
import { ReactComponent as SendIcon } from "../../assets/svg/send-icon.svg";
import { GRAY_400, PRIMARY_700, ERROR_700, WARNING_600, ERROR_600 } from "../../constant/colors";

const iconSizeStyle = `
  width: 20px;
  height: 20px;
`;

const GrayRemoveIcon = styled(RemoveIcon)`
  ${iconSizeStyle}
  path {
    stroke: ${GRAY_400};
  }
`;

const BlueSendIcon = styled(SendIcon)`
  ${iconSizeStyle}
  path {
    stroke: ${PRIMARY_700};
  }
`;

const RedCircleCloseIcon = styled(CircleCloseIcon)`
  ${iconSizeStyle}
  path {
    stroke: ${ERROR_700};
  }
`;

const OrangeAllCyclesIcon = styled(AllCyclesIcon)`
  ${iconSizeStyle}
  path {
    stroke: ${WARNING_600};
  }
`;

const RedAllCyclesIcon = styled(AllCyclesIcon)`
  ${iconSizeStyle}
  path {
    stroke: ${ERROR_600};
  }
`;

const StyledCircleCheckIcon = styled(CircleCheckIcon)`
  ${iconSizeStyle}
`;

/**
 * Helper component to render different types of consent events
 * Maps event types to appropriate icons, titles, and detail components
 */
const ConsentEventRenderer = ({ event, isHighlighted = false, canViewOrDownloadAttachments = false }) => {
  const { t } = useTranslation();

  const formatSubtitle = (event) => {
    const userName = getConsentActionUserName(event, t);
    const eventCreationDate = event.createdAt;
    const formattedDate = formatConsentEventTimestamp(eventCreationDate);
    return `${userName}, ${formattedDate}`;
  };

  const formatElectronicSubtitle = (event) => {
    const patientName = event.patientName || t("consent.forms.patient_fallback");
    const eventCreationDate = event.createdAt;
    const formattedDate = formatConsentEventTimestamp(eventCreationDate);
    return `${t("consent.history.electronic_consent_subtitle_prefix")} – ${patientName}, ${formattedDate}`;
  };

  const formatAutomaticSubtitle = (event) => {
    const eventCreationDate = event.createdAt;
    const formattedDate = formatConsentEventTimestamp(eventCreationDate);
    return `${t("consent.system")} – ${formattedDate} (automatic)`;
  };

  // Format action template messages for manual consent actions
  const formatActionTemplate = (action, event) => {
    // Translate the action type (e.g., "received" -> "Recorded")
    const actionText = t(`consent.forms.action_types.${action}`);

    let viaDisplayText = "";
    const option = CONSENT_VIA_SELECT_OPTIONS.find((opt) => opt.value === event.via);
    if (option?.historyTextDisplay) {
      viaDisplayText = option.historyTextDisplay(t);
    } else if (option) {
      viaDisplayText = t(option.label);
    } else {
      viaDisplayText = event.via;
    }

    return `${actionText} ${viaDisplayText}`;
  };

  const eventConfigs = {
    [CONSENT_RENDERING_TYPES.NO_RECORD]: {
      icon: <GrayRemoveIcon />,
      title: t("consent.history.no_record"),
      subtitle: null,
      status: CONSENT_STATUS_TYPES.NO_RECORD,
      statusText: t("consent.status.no_record"),
      renderDetails: null
    },
    [CONSENT_RENDERING_TYPES.LINK_SENT]: {
      icon: <BlueSendIcon />,
      title: t("consent.history.request_sent"),
      subtitle: formatSubtitle(event),
      status: CONSENT_STATUS_TYPES.PENDING,
      statusText: t("consent.status.pending_signature"),
      renderDetails: () => <ConsentRequestDetails phone={event.contactInfo?.phone} email={event.contactInfo?.email} />
    },
    [CONSENT_RENDERING_TYPES.CONSENT_RECORDED_MANUALLY]: {
      icon: <StyledCircleCheckIcon />,
      title: t("consent.history.consent_received"),
      subtitle: formatSubtitle(event),
      status: mapEventTypeToStatus(CONSENT_EVENT_TYPES.CONSENT_ACCEPTED),
      statusText: t("consent.status.consent_received"),
      renderDetails: () => (
        <ConsentActionDetails
          actionTemplate={formatActionTemplate(CONSENT_ACTION_TYPES.RECEIVED, event)}
          note={event.note}
          attachmentDetails={event.attachmentDetails}
          effectiveDate={event.effectiveDate}
          canViewOrDownloadAttachments={canViewOrDownloadAttachments}
        />
      )
    },
    [CONSENT_RENDERING_TYPES.CONSENT_RECEIVED_ELECTRONICALLY]: {
      icon: <StyledCircleCheckIcon />,
      title: t("consent.history.electronic_consent_received"),
      subtitle: formatElectronicSubtitle(event),
      status: mapEventTypeToStatus(CONSENT_EVENT_TYPES.CONSENT_ACCEPTED),
      statusText: t("consent.status.consent_received"),
      renderDetails: () => (
        <ConsentActionDetails
          note={event.note}
          attachmentDetails={event.attachmentDetails}
          effectiveDate={event.effectiveDate}
          canViewOrDownloadAttachments={canViewOrDownloadAttachments}
        />
      )
    },
    [CONSENT_RENDERING_TYPES.CONSENT_REVOKED]: {
      icon: <RedCircleCloseIcon />,
      title: t("consent.history.consent_revoked"),
      subtitle: formatSubtitle(event),
      status: mapEventTypeToStatus(CONSENT_EVENT_TYPES.CONSENT_REVOKED),
      statusText: t("consent.status.consent_revoked"),
      renderDetails: () => (
        <ConsentActionDetails
          actionTemplate={formatActionTemplate(CONSENT_ACTION_TYPES.REVOKED, event)}
          note={event.note}
          attachmentDetails={event.attachmentDetails}
          effectiveDate={event.effectiveDate}
          canViewOrDownloadAttachments={canViewOrDownloadAttachments}
        />
      )
    },
    [CONSENT_RENDERING_TYPES.LINK_EXPIRED]: {
      icon: <OrangeAllCyclesIcon />,
      title: t("consent.history.link_expired"),
      subtitle: formatAutomaticSubtitle(event),
      status: mapEventTypeToStatus(CONSENT_EVENT_TYPES.LINK_EXPIRED),
      statusText: t("consent.status.request_link_expired"),
      renderDetails: null
    },
    [CONSENT_RENDERING_TYPES.CONSENT_EXPIRED]: {
      icon: <RedAllCyclesIcon />,
      title: t("consent.history.consent_expired"),
      subtitle: formatAutomaticSubtitle(event),
      status: mapEventTypeToStatus(CONSENT_EVENT_TYPES.CONSENT_EXPIRED),
      statusText: t("consent.status.consent_expired"),
      renderDetails: null
    },
    // MVP-29825. Navigator-recorded, so it carries the same attribution
    // subtitle and note/attachment details as the other manual actions.
    [CONSENT_RENDERING_TYPES.CONSENT_DECLINED]: {
      icon: <RedCircleCloseIcon />,
      title: t("consent.history.consent_declined"),
      subtitle: formatSubtitle(event),
      status: mapEventTypeToStatus(CONSENT_EVENT_TYPES.CONSENT_DECLINED),
      statusText: t("consent.status.consent_declined"),
      renderDetails: () => (
        <ConsentActionDetails
          actionTemplate={formatActionTemplate(CONSENT_ACTION_TYPES.DECLINED, event)}
          note={event.note}
          attachmentDetails={event.attachmentDetails}
          effectiveDate={event.effectiveDate}
          canViewOrDownloadAttachments={canViewOrDownloadAttachments}
        />
      )
    }
  };

  const config = eventConfigs[mapServerEventTypeToDisplayRenderingType(event)];

  return config ? (
    <ConsentEventItem
      eventId={event.id}
      icon={config.icon}
      title={config.title}
      subtitle={config.subtitle}
      status={config.status}
      statusText={config.statusText}
      renderDetails={config.renderDetails}
      isHighlighted={isHighlighted}
    />
  ) : (
    <></>
  );
};

export default ConsentEventRenderer;
